/**
 * Upload Manager for handling file upload progress and state
 * Provides utilities for tracking multiple uploads and progress reporting
 */

export interface UploadState {
  id: string
  status: 'pending' | 'uploading' | 'completed' | 'error' | 'cancelled'
  progress: number
  startTime: number
  files: File[]
  error?: string
}

export interface UploadProgressEvent {
  uploadId: string
  progress: number
  loaded: number
  total: number
}

export type UploadProgressCallback = (event: UploadProgressEvent) => void
export type UploadStateChangeCallback = (uploadState: UploadState) => void

class UploadManager {
  private readonly uploads = new Map<string, UploadState>()
  private readonly progressCallbacks = new Map<string, UploadProgressCallback[]>()
  private readonly stateChangeCallbacks = new Map<string, UploadStateChangeCallback[]>()
  private readonly abortControllers = new Map<string, AbortController>()

  startUpload(files: File[]): string {
    const uploadId = this.generateUploadId()
    const uploadState: UploadState = {
      id: uploadId,
      status: 'pending',
      progress: 0,
      startTime: Date.now(),
      files: [...files]
    }

    this.uploads.set(uploadId, uploadState)
    this.progressCallbacks.set(uploadId, [])
    this.stateChangeCallbacks.set(uploadId, [])
    this.abortControllers.set(uploadId, new AbortController())

    this.notifyStateChange(uploadId)
    return uploadId
  }

  setProgress(uploadId: string, progress: number, loaded?: number, total?: number): void {
    const uploadState = this.uploads.get(uploadId)
    if (!uploadState) {
      console.warn(`Upload ${uploadId} not found`)
      return
    }

    uploadState.progress = Math.max(0, Math.min(100, progress))
    uploadState.status = progress >= 100 ? 'completed' : 'uploading'

    this.uploads.set(uploadId, uploadState)

    // Notify progress callbacks
    const callbacks = this.progressCallbacks.get(uploadId) || []
    callbacks.forEach((callback) => {
      callback({
        uploadId,
        progress: uploadState.progress,
        loaded: loaded ?? 0,
        total: total ?? 0
      })
    })

    this.notifyStateChange(uploadId)
  }

  completeUpload(uploadId: string): void {
    const uploadState = this.uploads.get(uploadId)
    if (!uploadState) return

    uploadState.status = 'completed'
    uploadState.progress = 100
    this.uploads.set(uploadId, uploadState)
    this.notifyStateChange(uploadId)
  }

  failUpload(uploadId: string, error: string): void {
    const uploadState = this.uploads.get(uploadId)
    if (!uploadState) return

    uploadState.status = 'error'
    uploadState.error = error
    this.uploads.set(uploadId, uploadState)
    this.notifyStateChange(uploadId)
  }

  cancelUpload(uploadId: string): void {
    const uploadState = this.uploads.get(uploadId)
    if (!uploadState) return

    const abortController = this.abortControllers.get(uploadId)
    if (abortController) {
      abortController.abort()
    }

    uploadState.status = 'cancelled'
    this.uploads.set(uploadId, uploadState)
    this.notifyStateChange(uploadId)
  }

  getAbortSignal(uploadId: string): AbortSignal | undefined {
    return this.abortControllers.get(uploadId)?.signal
  }

  getUploadState(uploadId: string): UploadState | undefined {
    return this.uploads.get(uploadId)
  }

  getAllUploads(): UploadState[] {
    return Array.from(this.uploads.values())
  }

  onProgress(uploadId: string, callback: UploadProgressCallback): () => void {
    const callbacks = this.progressCallbacks.get(uploadId) || []
    callbacks.push(callback)
    this.progressCallbacks.set(uploadId, callbacks)

    return () => {
      const currentCallbacks = this.progressCallbacks.get(uploadId) || []
      const index = currentCallbacks.indexOf(callback)
      if (index > -1) {
        currentCallbacks.splice(index, 1)
        this.progressCallbacks.set(uploadId, currentCallbacks)
      }
    }
  }

  onStateChange(uploadId: string, callback: UploadStateChangeCallback): () => void {
    const callbacks = this.stateChangeCallbacks.get(uploadId) || []
    callbacks.push(callback)
    this.stateChangeCallbacks.set(uploadId, callbacks)

    return () => {
      const currentCallbacks = this.stateChangeCallbacks.get(uploadId) || []
      const index = currentCallbacks.indexOf(callback)
      if (index > -1) {
        currentCallbacks.splice(index, 1)
        this.stateChangeCallbacks.set(uploadId, currentCallbacks)
      }
    }
  }

  cleanup(uploadId?: string): void {
    if (uploadId) {
      // Clean up specific upload
      this.uploads.delete(uploadId)
      this.progressCallbacks.delete(uploadId)
      this.stateChangeCallbacks.delete(uploadId)
      this.abortControllers.delete(uploadId)
    } else {
      // Clean up all completed/failed uploads
      const toDelete = Array.from(this.uploads.entries())
        .filter(([, state]) => state.status === 'completed' || state.status === 'error')
        .map(([id]) => id)

      toDelete.forEach((id) => this.cleanup(id))
    }
  }

  private generateUploadId(): string {
    return `upload_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`
  }

  private notifyStateChange(uploadId: string): void {
    const uploadState = this.uploads.get(uploadId)
    if (!uploadState) return

    const callbacks = this.stateChangeCallbacks.get(uploadId) || []
    callbacks.forEach((callback) => callback(uploadState))
  }
}

// Export singleton instance
export const uploadManager = new UploadManager()

export function createProgressHandler(
  uploadId: string,
  onProgress?: (percentage: number) => void
): (event: ProgressEvent) => void {
  return (event: ProgressEvent) => {
    if (event.lengthComputable) {
      const percentage = (event.loaded / event.total) * 100

      // Update upload manager
      uploadManager.setProgress(uploadId, percentage, event.loaded, event.total)

      if (onProgress) {
        onProgress(percentage)
      }
    }
  }
}

export function createFormData(data: Record<string, string | number | boolean | File | File[]>): FormData {
  const formData = new FormData()

  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      if (value instanceof File) {
        formData.append(key, value)
      } else if (Array.isArray(value)) {
        value.forEach((item) => {
          if (item instanceof File) {
            formData.append(key, item)
          } else {
            formData.append(key, String(item))
          }
        })
      } else if (typeof value === 'object') {
        formData.append(key, JSON.stringify(value))
      } else {
        formData.append(key, String(value))
      }
    }
  })

  return formData
}

export interface FileValidationOptions {
  maxSize?: number // in bytes
  allowedTypes?: string[] // MIME types
  maxFiles?: number
}

export interface FileValidationResult {
  valid: boolean
  errors: string[]
  validFiles: File[]
  invalidFiles: File[]
}

export function validateFiles(files: File[], options: FileValidationOptions = {}): FileValidationResult {
  const {
    maxSize = 100 * 1024 * 1024, // 100MB default
    allowedTypes = [],
    maxFiles = 10
  } = options

  const errors: string[] = []
  const validFiles: File[] = []
  const invalidFiles: File[] = []

  // Check file count
  if (files.length > maxFiles) {
    errors.push(`Maximum ${maxFiles} files allowed, but ${files.length} files selected`)
  }

  files.forEach((file) => {
    let fileValid = true

    // Check file size
    if (file.size > maxSize) {
      errors.push(
        `File "${file.name}" is too large (${formatFileSize(file.size)}). Maximum allowed size is ${formatFileSize(maxSize)}`
      )
      fileValid = false
    }

    // Check file type
    if (allowedTypes.length > 0 && !allowedTypes.includes(file.type)) {
      errors.push(`File "${file.name}" has unsupported type "${file.type}". Allowed types: ${allowedTypes.join(', ')}`)
      fileValid = false
    }

    if (fileValid) {
      validFiles.push(file)
    } else {
      invalidFiles.push(file)
    }
  })

  return {
    valid: errors.length === 0,
    errors,
    validFiles,
    invalidFiles
  }
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}
